import { useEffect, useRef } from 'react';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { useVoicePrefs } from './queries/useVoicePrefs';
import { useCoachReviewMode } from '../hooks/useCoachReviewMode';
import type { BrainTurn } from './brain';

interface Options {
  /** Brain turn list. Each new assistant turn is a candidate to speak. */
  turns: BrainTurn[];
  /** True when Brain is mid-thought — defer auto-speak until the
   *  reply has fully landed (loading=false + new assistant turn). */
  loading: boolean;
}

interface Result {
  /** No-op retained for API compatibility — voice output now fires for every
   *  reply when enabled, so callers no longer need to flag voice turns. */
  markVoiceTurn: () => void;
  /** Speak the given text on demand. Wired to the per-message replay
   *  button in chat threads. */
  speak: (text: string) => void;
  /** Stop any in-flight TTS. */
  stop: () => void;
  /** True while audio is playing. */
  isSpeaking: boolean;
  /** Whether voice output is on globally — drives mute toggles. */
  voiceEnabled: boolean;
  /** Flip voice_enabled. */
  toggleVoiceEnabled: () => void;
}

/**
 * Auto-speak for Brain replies. Every settled assistant turn is spoken when
 * voice output is enabled (global `voice_enabled` pref + mute toggle) —
 * matching onboarding, so the Brain has a voice everywhere in the app, not
 * only after voice input. Coach can `speak()` any earlier message on demand,
 * `stop()` mid-reply, or mute via `toggleVoiceEnabled()`.
 *
 * Centralises the logic so SessionNew / DrillNew / BrainChatPanel share one
 * rule. (Onboarding has its own equivalent inline effect.)
 */
export function useBrainAutoSpeak({ turns, loading }: Options): Result {
  const { prefs, save } = useVoicePrefs();
  const tts = useTextToSpeech({
    provider: prefs.voice_provider,
    rate: prefs.voice_rate,
    voiceId: prefs.voice_id,
  });
  const reviewMode = useCoachReviewMode();

  // Tracks which turn id was last spoken so we don't double-fire on
  // re-renders.
  const spokenTurnIdRef = useRef<string | null>(null);
  // Tracks which assistant turn id was last logged to coach_review_log
  // so we don't double-insert. (Used only in review mode.)
  const loggedTurnIdRef = useRef<string | null>(null);

  const lastTurn = turns[turns.length - 1] ?? null;

  useEffect(() => {
    if (loading) return;
    if (!lastTurn || lastTurn.role !== 'assistant') return;
    if (spokenTurnIdRef.current === lastTurn.id) return;
    spokenTurnIdRef.current = lastTurn.id;
    if (!prefs.voice_enabled) return;
    const text = lastTurn.text?.trim();
    if (!text) return;
    // Speak every settled reply when voice is on — matches onboarding, so the
    // Brain has a voice everywhere, not only after voice input. Mute toggle +
    // the voice_enabled pref are the opt-out.
    tts.speakSentenceChunked(text);
  }, [lastTurn, loading, prefs.voice_enabled, tts]);

  // Review-mode capture — every settled Brain reply pairs with the
  // coach's last utterance and gets logged + async-classified. Fires
  // ONLY in review mode (the hook is a no-op otherwise so the regular
  // coach experience is unaffected).
  useEffect(() => {
    if (!reviewMode.isReviewMode) return;
    if (loading) return;
    if (!lastTurn || lastTurn.role !== 'assistant') return;
    if (loggedTurnIdRef.current === lastTurn.id) return;
    // Pair this assistant turn with the most recent user turn.
    let coachText: string | null = null;
    for (let i = turns.length - 2; i >= 0; i--) {
      const t = turns[i];
      if (t.role === 'user' && t.text?.trim()) {
        coachText = t.text.trim();
        break;
      }
    }
    if (!coachText) return;
    loggedTurnIdRef.current = lastTurn.id;
    void reviewMode.logUtterance(coachText, lastTurn.text?.trim() ?? null);
  }, [lastTurn, loading, reviewMode, turns]);

  return {
    // No-op retained for API compatibility — voice output now fires for every
    // reply when enabled, so voice-input handlers no longer need to gate it.
    markVoiceTurn: () => {},
    speak: tts.speakSentenceChunked,
    stop: tts.stop,
    isSpeaking: tts.isSpeaking,
    voiceEnabled: prefs.voice_enabled,
    toggleVoiceEnabled: () => {
      tts.stop();
      void save({ voice_enabled: !prefs.voice_enabled });
    },
  };
}
